import router from './router'
import axios from 'axios'
import Vue from 'vue'
import ElementUI from 'element-ui'
import locale from 'element-ui/lib/locale/lang/en'
import 'element-ui/lib/theme-chalk/index.css'
import App from './App.vue'
import '@/assets/base.css'
import '@/assets/listPage.css'
import qs from 'qs';
import store from './store/store.js';
import * as Sentry from '@sentry/vue';
import NP from 'number-precision'
import {getCurrentCompanyUUID} from './utils/company';
import i18n from './i18n'
import {requestEmailVerification} from './utils/emailVerificationDialog'
import {containsHtml} from './utils/emailContent'
import {
    clearSentryUser,
    reportHandledError,
    syncSentryUserFromStorage
} from './utils/sentry'
import {
    invalidateCurrentTabAuthentication,
    isCurrentTabAuthenticationInvalidated,
    shouldInvalidateForLoginStorageEvent
} from './utils/authTabState'


Vue.config.productionTip = false;
Vue.use(ElementUI, {locale});
Vue.use(i18n);


/*axios配置部分*/
let axiosIns = axios.create({});
if (process.env.NODE_ENV == 'development') {
    // axiosIns.defaults.baseURL = 'http://dev.zuliup.cn/';
    axiosIns.defaults.baseURL = 'https://testapi.goodinvoice.com/';
} else if (process.env.NODE_ENV == 'production') {
    axiosIns.defaults.baseURL = 'https://service.goodinvoice.com/';
    Sentry.init({
        Vue: Vue,
        dsn: 'https://bf07658628384433972e2bb4ed476541@sentry.io/1809464',
        attachProps: false,
        logErrors: true,
    });
    syncSentryUserFromStorage();
}
store.commit("setRequestURL", axiosIns.defaults.baseURL);
axiosIns.defaults.responseType = 'json';
axiosIns.defaults.transformRequest = [
    function (data) {
        //数据序列化
        return qs.stringify(data);
    }
];
axiosIns.defaults.timeout = 10000;
const protectedRequestCancels = new Set();

function attachProtectedRequestCancel(config) {
    config.cancelToken = new axios.CancelToken(function (cancel) {
        protectedRequestCancels.add(cancel);
        config.__protectedRequestCancel = cancel;
    });
}

function removeProtectedRequestCancel(config) {
    if (config != null && config.__protectedRequestCancel != null) {
        protectedRequestCancels.delete(config.__protectedRequestCancel);
        delete config.__protectedRequestCancel;
    }
}

function cancelProtectedRequests() {
    protectedRequestCancels.forEach(function (cancel) {
        cancel('Authentication changed in another tab.');
    });
    protectedRequestCancels.clear();
}

function replaceWithLogin() {
    if (router.currentRoute.path !== '/login') {
        router.replace('/login');
    }
}

window.addEventListener('storage', function (event) {
    if (!shouldInvalidateForLoginStorageEvent(event)) {
        return;
    }
    invalidateCurrentTabAuthentication();
    cancelProtectedRequests();
    clearSentryUser();
    replaceWithLogin();
});

axiosIns.interceptors.request.use(function (config) {
    syncSentryUserFromStorage();
    let requestUrl = config.url;
    // console.log(requestUrl)

    if ((!localStorage.getItem("loginInformation") || isCurrentTabAuthenticationInvalidated())
        && !isPublicAuthRequest(requestUrl)) {
        ElementUI.Message({
            type: "error",
            message: i18n.t('network.loginExpired'),
            duration: "2000",
        });
        replaceWithLogin();
        return Promise.reject(new axios.Cancel('Authentication is no longer valid in this tab.'));
    }
    if (isEmailSendRequest(requestUrl) && emailRequestContainsHtml(config.data.params)) {
        const error = new Error(i18n.t('common.emailHtmlNotAllowed'));
        error.emailHtmlNotAllowed = true;
        return Promise.reject(error);
    }
    if (isEmailSendRequest(requestUrl) && config.__emailVerificationOriginalParams == null) {
        config.__emailVerificationOriginalParams = JSON.parse(JSON.stringify(config.data.params));
    }
    //配置config
    config.headers.Accept = 'application/json';
    if (!isPublicAuthRequest(requestUrl)) {
        attachProtectedRequestCancel(config);
    }
    appendCompanyUUID(config.data.params, requestUrl);
    config.data = {params: aes_encrypted(config.data.params, requestUrl)}
    return config;
});
axiosIns.interceptors.response.use(function (response) {
    removeProtectedRequestCancel(response.config);
    let status = response.status;
    if (status === 200) {
        const contentType = response.headers['content-type'];
        if (contentType != null
            && (contentType.indexOf('application/pdf') !== -1
                || contentType.indexOf('text/csv') !== -1)) {
            return Promise.resolve(response);
        }
        if (response.data.errorCode == '216') {
            clearSentryUser();
            invalidateCurrentTabAuthentication();
            cancelProtectedRequests();
            localStorage.clear();
            replaceWithLogin();

            ElementUI.Message({
                type: "error",
                message: i18n.t('network.loginExpired'),
                duration: "2000",
            });
            return;
        }
        if (Number(response.data.errorCode) === 308
            && isEmailSendRequest(response.config.url)
            && !response.config.__emailVerificationRetried) {
            return requestEmailVerification(response.data.objectData).then(function (verified) {
                if (!verified) {
                    return response;
                }
                const retryConfig = Object.assign({}, response.config, {
                    data: {params: response.config.__emailVerificationOriginalParams},
                    __emailVerificationRetried: true
                });
                return axiosIns.request(retryConfig);
            });
        }
        //业务逻辑错误,统一处理
        if (response.data.errorCode != 0
            && Number(response.data.errorCode) !== 309
            && Number(response.data.errorCode) !== 310) {
            reportApiResponseError(response);
            ElementUI.Message({
                type: "error",
                message: response.data.errorMsg,
                duration: "2000",
            });
        }
        return Promise.resolve(response);
    } else {
        return Promise.reject(response);
    }
}, function (error) {
    removeProtectedRequestCancel(error == null ? null : error.config);
    if (axios.isCancel(error)) {
        return Promise.reject(error);
    }
    if (error != null && error.emailHtmlNotAllowed) {
        ElementUI.Message({
            type: "error",
            message: error.message,
            duration: "2000",
        });
        return Promise.resolve({
            data: {
                errorCode: 311,
                errorMsg: error.message,
                objectData: null
            }
        });
    }
    reportRequestError(error);
    // 响应错误提示
    ElementUI.Message({
        type: "error",
        message: i18n.t('network.requestTimeout'),
        duration: "700",
    });
    return Promise.reject(error);
});

const axiosPost = axiosIns.post.bind(axiosIns);
let pendingSettingAndCompanyRequest = null;

axiosIns.post = function (url, data, config) {
    if (url !== 'query_setting_and_company') {
        return axiosPost(url, data, config);
    }
    if (pendingSettingAndCompanyRequest != null) {
        return pendingSettingAndCompanyRequest;
    }

    const request = axiosPost(url, data, config);
    pendingSettingAndCompanyRequest = request;
    const clearPendingRequest = function () {
        if (pendingSettingAndCompanyRequest === request) {
            pendingSettingAndCompanyRequest = null;
        }
    };
    request.then(clearPendingRequest, clearPendingRequest);
    return request;
};

NP.enableBoundaryChecking(false); // default param is true

Vue.prototype.$axios = axiosIns;
Vue.prototype.$NP = NP;
Vue.prototype.$reportHandledError = reportHandledError;

function requestEndpoint(requestUrl) {
    if (requestUrl == null || requestUrl === '') {
        return 'unknown';
    }
    return String(requestUrl).split('?')[0];
}

function reportApiResponseError(response) {
    const errorCode = response.data.errorCode;
    const endpoint = requestEndpoint(response.config.url);
    if (Number(errorCode) === 216
        || Number(errorCode) === 308
        || Number(errorCode) === 309
        || Number(errorCode) === 310
        || Number(errorCode) === 311
        || isPublicAuthRequest(endpoint)) {
        return;
    }

    const errorMessage = response.data.errorMsg == null || response.data.errorMsg === ''
        ? `API ${endpoint} returned errorCode ${errorCode}`
        : String(response.data.errorMsg);
    reportHandledError(new Error(errorMessage), {
        module: 'api',
        operation: endpoint,
        tags: {
            error_code: errorCode,
            http_status: response.status
        },
        context: {
            endpoint: endpoint,
            errorCode: errorCode
        }
    });
}

function reportRequestError(error) {
    if (axios.isCancel(error)) {
        return;
    }
    const response = error == null ? null : error.response;
    const config = error == null ? null : error.config;
    const endpoint = requestEndpoint(config == null ? null : config.url);
    reportHandledError(error, {
        level: 'error',
        module: 'api',
        operation: endpoint,
        tags: {
            http_status: response == null ? 'network_error' : response.status
        },
        context: {
            endpoint: endpoint,
            method: config == null || config.method == null ? '' : String(config.method).toUpperCase(),
            status: response == null ? null : response.status
        }
    });
}

function isPublicAuthRequest(requestUrl) {
    return requestUrl === 'login.htm'
        || requestUrl === 'create_invoice_user'
        || requestUrl === 'send_reset_password_email'
        || requestUrl === 'social_login'
        || requestUrl === 'bind_social_login';
}

function isEmailSendRequest(requestUrl) {
    return typeof requestUrl === 'string'
        && (requestUrl === 'send_email_to_client' || requestUrl.endsWith('/send_email_to_client'));
}

function emailRequestContainsHtml(params) {
    if (params == null) {
        return false;
    }
    return containsHtml(params.subject)
        || containsHtml(params.content);
}

function shouldAppendTopLevelCompanyUUID(requestUrl) {
    if (requestUrl === 'web_export_data') {
        return true;
    }
    if (requestUrl === 'get_auth') {
        return true;
    }
    if (requestUrl === 'create_tax_info' || (requestUrl != null && requestUrl.endsWith('/create_tax_info'))) {
        return true;
    }
    if (requestUrl === 'login.htm'
        || requestUrl === 'login_out'
        || requestUrl === 'create_invoice_user'
        || requestUrl === 'send_reset_password_email'
        || requestUrl === 'social_login'
        || requestUrl === 'bind_social_login'
        || requestUrl === 'web_create_company'
        || requestUrl === 'query_setting_and_company'
        || requestUrl === 'update_setting_and_company'
        || requestUrl === 'query_total_outstanding_balance') {
        return false;
    }
    if (requestUrl != null && requestUrl.endsWith('/update_setting_and_company')) {
        return false;
    }
    if (requestUrl === 'web_create_invoice' || requestUrl === 'web_create_expense') {
        return false;
    }
    return requestUrl.indexOf('query_') !== -1 || requestUrl.indexOf('create_') !== -1 || requestUrl.indexOf('update_') !== -1;
}

function addCompanyUUIDToObject(target, companyUUID) {
    if (target == null || Array.isArray(target) || typeof target !== 'object') {
        return;
    }
    target.companyUUID = companyUUID;
}

function addCompanyUUIDToList(targetList, companyUUID) {
    if (!Array.isArray(targetList)) {
        return;
    }
    for (let i = 0; i < targetList.length; i++) {
        addCompanyUUIDToObject(targetList[i], companyUUID);
    }
}

function appendCompanyUUIDForSaveRequest(params, requestUrl, companyUUID) {
    if (requestUrl === 'update_setting_and_company'
        || (requestUrl != null && requestUrl.endsWith('/update_setting_and_company'))) {
        addCompanyUUIDToObject(params.settingReq, companyUUID);
        addCompanyUUIDToObject(params.companyReq, companyUUID);
        return;
    }
    if (requestUrl === 'web_create_invoice') {
        addCompanyUUIDToList(params.itemList, companyUUID);
        addCompanyUUIDToObject(params.userClient, companyUUID);
        addCompanyUUIDToList(params.payHistoryList, companyUUID);
        addCompanyUUIDToList(params.invoiceImageList, companyUUID);
        addCompanyUUIDToList(params.taxInfoList, companyUUID);
        addCompanyUUIDToObject(params.invoice, companyUUID);
        if (params.invoice != null && !Array.isArray(params.invoice) && typeof params.invoice === 'object') {
            params.invoice.clientUUID = params.invoice.clientUUIDKey;
            if (Array.isArray(params.invoiceImageList)) {
                for (let i = 0; i < params.invoiceImageList.length; i++) {
                    params.invoiceImageList[i].invoiceUUID = params.invoice.innerUUID;
                }
            }
        }
        return;
    }

    if (requestUrl === 'web_create_expense') {
        addCompanyUUIDToObject(params.expense, companyUUID);
        addCompanyUUIDToList(params.categoryList, companyUUID);
        addCompanyUUIDToObject(params.companyLogo, companyUUID);
    }
}

function appendCompanyUUID(params, requestUrl) {
    const companyUUID = getCurrentCompanyUUID();
    if (companyUUID === '') {
        return;
    }

    if (shouldAppendTopLevelCompanyUUID(requestUrl)) {
        params.companyUUID = companyUUID;
    }
    appendCompanyUUIDForSaveRequest(params, requestUrl, companyUUID);
}

function aes_encrypted(encryptedParams, requestUrl) {
    // console.log(requestUrl)
    let milliseconds = Date.parse(new Date());
    if (localStorage.getItem("loginInformation")) {
        let loginInformation = JSON.parse(localStorage.getItem("loginInformation")).objectData;
        if (loginInformation) {
            encryptedParams.userUuid = loginInformation.userUuid;
            if (encryptedParams.email == null) {
                encryptedParams.email = loginInformation.email;
            }
        }
    } else {
        if (!isPublicAuthRequest(requestUrl)) {

            ElementUI.Message({
                type: "error",
                message: i18n.t('network.loginExpired'),
                duration: "2000",
            });
            replaceWithLogin();
        }

    }

    encryptedParams.reqToken = milliseconds;
    encryptedParams.clientInfo = navigator.userAgent + '|7.0.0';
    encryptedParams.language = i18n.locale;
    encryptedParams.fromClient = 3;
    return JSON.stringify(encryptedParams);
}


new Vue({
    render: h => h(App),
    router,
    store
}).$mount('#app')
